import {useState, useMemo} from 'react'
import {Stack} from '@/components/shared/Stack'
import {useNavigate} from 'react-router-dom'
import {startAction} from '@/observability/session'
import {useQuery} from '@tanstack/react-query'
import {parseCronExpression} from '@/lib/cron-utils'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {MutedText} from '@/components/shared/MutedText'
import {ErrorText} from '@/components/shared/ErrorText'
import {useCreateInboundConnector, useInboundConnectors} from '@/hooks/use-inbound-connectors'
import {routes} from '@/lib/routes'
import {InboundConnectorType, InboundConnectorTypeBadge} from '@/types/inbound-connector'
import type {InboundConnectorCreateRequest} from '@/types/inbound-connector'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {FormGrid} from '@/components/shared/FormGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {
    Select,
    SelectContent,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'

interface CreateInboundConnectorDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const DEFAULT_CRON = '0 0 */4 * * *'

export function CreateInboundConnectorDialog({open, onOpenChange}: CreateInboundConnectorDialogProps) {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const createMutation = useCreateInboundConnector()

    const [displayName, setDisplayName] = useState('')
    const [targetSystemId, setTargetSystemId] = useState<string | null>(null)
    const [connectorType, setOutboundConnectorType] = useState<string>(InboundConnectorType.SCIM_2_0)
    const [cronExpression, setCronExpression] = useState(DEFAULT_CRON)
    const [enabled, setEnabled] = useState(true)
    const [submitted, setSubmitted] = useState(false)

    const [systemSearchQuery, setSystemSearchQuery] = useState('')

    const {data: existingConnectors} = useInboundConnectors('', {enabled: open})

    const {data: systemSearchData, isLoading: systemsLoading} = useQuery({
        queryKey: ['systems', 'search', systemSearchQuery],
        queryFn: () => services.systems.list({q: systemSearchQuery, size: 20}),
        enabled: open,
    })

    const systemOptions: ComboboxOption[] = useMemo(() => {
        const existingSystemIds = new Set(
            (existingConnectors?.content ?? []).map(c => c.targetSystem.id)
        )
        return (systemSearchData?.content ?? [])
            .filter(s => !existingSystemIds.has(s.id))
            .map((s) => ({
                value: s.id,
                label: s.name,
                description: s.environment,
            }))
    }, [systemSearchData, existingConnectors])

    const cronParsed = useMemo(() => parseCronExpression(cronExpression, {use24HourTimeFormat: true}), [cronExpression])
    const cronPreview = cronParsed.description
    const isCronValid = cronExpression.trim() !== '' && cronParsed.description !== null

    const displayNameError = submitted && !displayName.trim() ? i18n.inboundConnectors.displayNameRequired : null
    const targetSystemError = submitted && !targetSystemId ? i18n.inboundConnectors.targetSystemRequired : null
    const cronError = submitted && cronExpression.trim() && !isCronValid ? i18n.inboundConnectors.invalidCronExpression : null

    const resetForm = () => {
        setDisplayName('')
        setTargetSystemId(null)
        setOutboundConnectorType(InboundConnectorType.SCIM_2_0)
        setCronExpression(DEFAULT_CRON)
        setEnabled(true)
        setSubmitted(false)
        setSystemSearchQuery('')
    }

    const handleSubmit = () => {
        setSubmitted(true)

        if (!displayName.trim() || !targetSystemId || (cronExpression.trim() && !isCronValid)) {
            return
        }

        const request: InboundConnectorCreateRequest = {
            displayName: displayName.trim(),
            targetSystemId,
            connectorType,
            cronExpression: cronExpression.trim() || null,
            description: null,
            enabled,
        }

        // eslint-disable-next-line i18next/no-literal-string
        const endAction = startAction('submit-create-inbound-connector')
        createMutation.mutate(request, {
            onSuccess: (data) => {
                endAction()
                resetForm()
                onOpenChange(false)
                navigate(routes.connectors.inbound.detail(data.id))
            },
            onError: () => {
                endAction()
            },
        })
    }

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            resetForm()
        }
        onOpenChange(isOpen)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.createConnector}</DialogTitle>
                    <DialogDescription>
                        {i18n.inboundConnectors.createDescription}
                    </DialogDescription>
                </DialogHeader>
                <FormGrid className="py-4">
                    <FormGrid>
                        <Label htmlFor="inbound-display-name">{i18n.inboundConnectors.displayName}</Label>
                        <Input
                            id="inbound-display-name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder={i18n.inboundConnectors.displayNamePlaceholder}
                            maxLength={255}
                            data-testid="inbound-display-name-input"
                        />
                        {displayNameError && (
                            <ErrorText data-testid="inbound-display-name-error">
                                {displayNameError}
                            </ErrorText>
                        )}
                    </FormGrid>
                    <FormGrid>
                        <Label>{i18n.inboundConnectors.targetSystem}</Label>
                        <Combobox
                            options={systemOptions}
                            value={targetSystemId}
                            onValueChange={setTargetSystemId}
                            onSearch={setSystemSearchQuery}
                            placeholder={i18n.inboundConnectors.selectSystem}
                            emptyMessage={i18n.inboundConnectors.allSystemsHaveConnectors}
                            isLoading={systemsLoading}
                            data-testid="inbound-target-system-combobox"
                        />
                        {targetSystemError && (
                            <ErrorText data-testid="inbound-target-system-error">
                                {targetSystemError}
                            </ErrorText>
                        )}
                    </FormGrid>
                    <FormGrid>
                        <Label>{i18n.inboundConnectors.connectorType}</Label>
                        <Select value={connectorType} onValueChange={setOutboundConnectorType}>
                            <SelectTrigger data-testid="inbound-connector-type-select">
                                <SelectValue placeholder={i18n.inboundConnectors.selectType}/>
                            </SelectTrigger>
                            <SelectContent>
                                <EnumSelectItems enumClass={InboundConnectorTypeBadge}/>
                            </SelectContent>
                        </Select>
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor="inbound-cron">{i18n.inboundConnectors.schedule}</Label>
                        <Input
                            id="inbound-cron"
                            value={cronExpression}
                            onChange={(e) => setCronExpression(e.target.value)}
                            placeholder={i18n.inboundConnectors.schedulePlaceholder}
                            data-testid="inbound-cron-input"
                        />
                        {cronPreview && (
                            <MutedText size="sm" data-testid="inbound-cron-preview">
                                {i18n.inboundConnectors.schedulePreview({preview: cronPreview})}
                            </MutedText>
                        )}
                        {cronError && (
                            <ErrorText data-testid="inbound-cron-error">
                                {cronError}
                            </ErrorText>
                        )}
                    </FormGrid>
                    <InlineRow justify="between">
                        <Stack gap="xs">
                            <Label>{i18n.inboundConnectors.enabledLabel}</Label>
                            <MutedText size="sm">
                                {i18n.inboundConnectors.enabledDescription}
                            </MutedText>
                        </Stack>
                        <Switch
                            checked={enabled}
                            onCheckedChange={setEnabled}
                            data-testid="inbound-enabled-switch"
                        />
                    </InlineRow>
                </FormGrid>
                <SaveCancelFooter
                    testIdPrefix="create-inbound"
                    onCancel={() => handleOpenChange(false)}
                    saving={createMutation.isPending}
                    saveLabel={i18n.common.create}
                    savingLabel={i18n.common.creating}
                    saveType="button"
                    onSave={handleSubmit}
                />
            </DialogContent>
        </Dialog>
    )
}
